import { useHasPermission } from '@/features/access-control';
import type { PermissionMode } from '@/features/access-control';
import { useToast } from '@/legacy/hooks';
import { commonTradPrefix } from '@/legacy/i18n';
import type { Permission } from '@/shared/constants';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router';
import { paths } from '../router/path';
import { Skeleton } from '@/shared/ui/skeleton';

type PermissionGuardProps = {
	permission: Permission | Permission[];
	mode?: PermissionMode;
	children?: React.ReactNode;
};

const PermissionGuard = ({ permission, mode = 'all', children }: PermissionGuardProps) => {
	const { hasPermission, isLoading } = useHasPermission(permission, mode);
	const { showToast } = useToast();
	const { t } = useTranslation();

	useEffect(() => {
		if (!isLoading && !hasPermission) {
			showToast(t(`${commonTradPrefix}.url_permission_denied`), 'warning');
		}
	}, [isLoading, hasPermission, showToast, t]);

	if (isLoading)
		return (
			<div className='flex flex-col gap-4 p-6 w-full'>
				<Skeleton
					height={28}
					width='35%'
					borderRadius={8}
				/>
				<Skeleton
					height={16}
					count={2}
					borderRadius={6}
				/>
				<Skeleton
					height={180}
					borderRadius={12}
				/>
			</div>
		);

	if (!hasPermission)
		return (
			<Navigate
				to={paths.home()}
				replace
			/>
		);

	if (children) return <>{children}</>;

	return <Outlet />;
};

export default PermissionGuard;
